import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Logo } from '../../components/common/Logo';
import bg from '../../assets/bg-register.jpg';
import { Loader2, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { FirebaseAuthForm } from '../../components/auth/FirebaseAuthForm';

export const RegisterPage = () => {
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const { isAuthenticated, firebaseUser, isInitialized } = useAuth();

  useEffect(() => {
    const checkAuth = () => {
      // Wait for auth to initialize
      if (!isInitialized) {
        return;
      }

      // If user is already signed in, redirect to home
      if (isAuthenticated && firebaseUser) {
        navigate('/home');
        return;
      }

      // If not signed in, show the signup form
      setIsChecking(false);
    };

    checkAuth();
  }, [isAuthenticated, firebaseUser, isInitialized, navigate]);

  if (isChecking) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-[#5CD2F4] animate-spin mx-auto" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="h-screen flex flex-col items-center justify-center px-3 sm:px-4 py-4 sm:py-6">
        <div className="w-full max-w-sm sm:max-w-md space-y-4 sm:space-y-6 flex flex-col justify-center">
          {/* Logo section */}
          <div className="flex justify-center mb-8">
            <div className="scale-150">
              <Logo />
            </div>
          </div>
          
          {/* Header with titles */}
          <div className="space-y-4 text-center">
            <div className="space-y-2">
              <h2 className="text-base sm:text-base md:text-base mb-4 sm:mb-6 text-gray-300 font-bold leading-normal text-center tracking-tight">
                Train your brain to execute like an elite entrepreneur in just 10 minutes a day.
              </h2>
              <p className="text-xs sm:text-base text-gray-300 font-montserrat font-medium">
                Patent-pending. Powered by AI & Neuroscience.
              </p>
            </div>
          </div>
  
          {/* Firebase SignUp Component */}
          <div className="space-y-4 flex flex-col items-center w-full">
            {!isAuthenticated ? (
              <div className="w-full max-w-md">
                <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2 justify-center">
                  <Mail className="text-[#5CD2F4]" size={20} />
                  Create Account
                </h3>
                
                <FirebaseAuthForm 
                  mode="signup"
                  onSuccess={async () => {
                    // After signup, check if user profile exists to determine if it's a new user
                    try {
                      const { AuthApiManager } = await import('../../services/apiManager/AuthApiManager');
                      const authApi = AuthApiManager.getInstance();
                      
                      // Get user ID from localStorage (set by handleEmailPasswordAuth)
                      const userId = localStorage.getItem('nuroleap_user_id');
                      if (userId) {
                        const profileResponse = await authApi.checkUserProfile(userId);
                        // If no profile exists, it's a new user -> onboarding
                        if (!profileResponse.success || !profileResponse.data) {
                          navigate('/onboarding');
                        } else {
                          // User already has profile, go to home
                          navigate('/home');
                        }
                      } else {
                        // Fallback: assume new user for signup
                        navigate('/onboarding');
                      }
                    } catch (error) {
                      console.error('Error checking user profile:', error);
                      // Fallback: assume new user for signup
                      navigate('/onboarding');
                    }
                  }}
                  onError={(error) => {
                    console.error('Registration error:', error);
                  }}
                />
                
                <div className="mt-4 text-center">
                  <Link
                    to="/login"
                    className="text-[#5CD2F4] hover:text-[#5CD2F4]/80 font-semibold hover:underline transition-colors"
                  >
                    Already have an account? Sign in
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center text-white">
                <p>You are already signed in.</p>
                <button
                  onClick={() => navigate('/home')}
                  className="mt-4 primary-button"
                >
                  Go to Home
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );  
};